import { createTheme } from "@mui/material/styles";
import { blur, colors, fonts, gradients, radii, shadows } from "./@shared/ui/tokens";

export const theme = {
  palette: {
    primary: {
      main: "#0070f7",
      dark: "#1135b7",
      light: "#30d8fc",
    },
    secondary: {
      main: "#ef169c",
      dark: "#b90c98",
      light: "#f96ed0",
    },
    tirciary: {
      main: "#371793",
      dark: "#16116e",
      light: "#6945d7",
    },
    text: {
      main: "#fee399",
      dark: "#000000",
      light: "#ffffff",
    },
    border: {
      main: "#f38153",
      dark: "#d36839",
      light: "#f7a378",
    },
    error: {
      main: "#f44336",
      dark: "#d32f2f",
      light: "#e57373",
    },
    warning: {
      main: "#ff9800",
      dark: "#f57c00",
      light: "#ffe082",
    },
    info: {
      main: "#2196f3",
      dark: "#1976d2",
      light: "#90caf9",
    },
    success: {
      main: "#4caf50",
      dark: "#2e7d32",
      light: "#c8e6c9",
    },
    black: "#000000",
    white: "#ffffff",
    grey: {
      main: "#808080",
      light: "#d3d3d3",
      dark: "#606060",
    },
  },
  typography: {
    fontFamily: `"Poppins", sans-serif;`,
  },
  zIndex: {
    mobileStepper: 1000,
    fab: 1050,
    speedDial: 1050,
    appBar: 1100,
    drawer: 1200,
    modal: 1300,
    snackbar: 1400,
    tooltip: 1500,
    toast: 9999, // custom zIndex for your Toast
  },
};

const darkPaperSx = {
  backgroundColor: colors.surfaceSolid,
  backgroundImage: `${gradients.panelSheen}, ${gradients.panel}`,
  color: colors.text,
  border: `1px solid ${colors.lineStrong}`,
  boxShadow: shadows.panel,
};

// Menu-and-screen chrome shared by every route except the gameboard, which
// keeps the plain `theme` so live matches are untouched.
export const uiTheme = {
  ...theme,
  typography: {
    fontFamily: fonts.body,
    h1: { fontFamily: fonts.display, fontWeight: 400, letterSpacing: "0.01em" },
    h2: { fontFamily: fonts.display, fontWeight: 400, letterSpacing: "0.01em" },
    h3: { fontFamily: fonts.display, fontWeight: 400, letterSpacing: "0.015em" },
    h4: { fontFamily: fonts.display, fontWeight: 400, letterSpacing: "0.02em" },
    h5: { fontFamily: fonts.display, fontWeight: 400, letterSpacing: "0.02em" },
    button: { fontWeight: 700, letterSpacing: "0.04em" },
  },
  components: {
    MuiBackdrop: {
      styleOverrides: {
        root: {
          "&:not(.MuiBackdrop-invisible)": {
            backgroundColor: "rgba(5, 2, 14, 0.72)",
            backdropFilter: "blur(6px)",
            WebkitBackdropFilter: "blur(6px)",
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: radii.lg,
        },
      },
    },
    MuiPopover: {
      styleOverrides: {
        paper: {
          ...darkPaperSx,
          borderRadius: radii.md,
          backdropFilter: blur.glass,
          marginTop: 6,
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          ...darkPaperSx,
          borderRadius: radii.md,
          minWidth: 180,
        },
        list: { padding: 6 },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          borderRadius: radii.sm,
          fontSize: "0.9rem",
          fontWeight: 600,
          minHeight: 38,
          transition: "background-color 140ms ease, color 140ms ease",
          "&:hover": { backgroundColor: "rgba(124, 92, 255, 0.22)" },
          "&.Mui-selected": {
            backgroundColor: "rgba(255, 211, 106, 0.16)",
            color: colors.goldBright,
          },
          "&.Mui-selected:hover": {
            backgroundColor: "rgba(255, 211, 106, 0.24)",
          },
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          background: "rgba(14, 7, 32, 0.96)",
          border: `1px solid ${colors.lineStrong}`,
          borderRadius: radii.sm,
          boxShadow: shadows.raised,
          fontSize: "0.75rem",
          fontWeight: 600,
          padding: "6px 10px",
        },
        arrow: { color: "rgba(14, 7, 32, 0.96)" },
      },
    },
    MuiBadge: {
      styleOverrides: {
        badge: {
          fontWeight: 800,
          fontSize: "0.68rem",
          boxShadow: "0 0 0 2px #0d0720, 0 4px 10px rgba(0,0,0,0.4)",
        },
        colorError: {
          background: "linear-gradient(180deg, #ff6b85 0%, #e8304f 100%)",
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          height: 3,
          borderRadius: 3,
          background: gradients.gold,
          boxShadow: shadows.glowGold,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          fontWeight: 700,
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          fontSize: "0.78rem",
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: { borderRadius: radii.pill, height: 8 },
        bar: { borderRadius: radii.pill },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: { borderColor: colors.lineStrong },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        switchBase: {
          "&.Mui-checked": { color: colors.gold },
          "&.Mui-checked + .MuiSwitch-track": {
            backgroundColor: colors.goldDeep,
            opacity: 0.8,
          },
        },
      },
    },
  },
};

export const uiMuiTheme = createTheme(uiTheme);

// For panels that sit on dark glass (auth, forms inside dark modals): inputs,
// labels and paper switch to their dark-mode defaults.
export const darkSurfaceMuiTheme = createTheme({
  ...uiTheme,
  palette: {
    ...theme.palette,
    mode: "dark",
    primary: { main: colors.violetBright, dark: colors.violet, light: "#c2b3ff", contrastText: "#ffffff" },
    background: { default: colors.void, paper: colors.surfaceSolid },
    text: { ...theme.palette.text, primary: "#ffffff", secondary: colors.textSoft },
  },
  components: {
    ...uiTheme.components,
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: radii.md,
          backgroundColor: "rgba(255,255,255,0.04)",
          "& .MuiOutlinedInput-notchedOutline": { borderColor: colors.lineStrong },
          "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,0.32)" },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: colors.gold,
            boxShadow: "0 0 0 3px rgba(255,211,106,0.15)",
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: { color: colors.textMuted, "&.Mui-focused": { color: colors.gold } },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          ...uiTheme.components.MuiTab.styleOverrides.root,
          color: colors.textMuted,
          "&.Mui-selected": { color: colors.goldBright },
        },
      },
    },
  },
});
export const baseMuiTheme = createTheme(theme);
